import { Calc } from "../math/Calc.js";

export class Text {

    private static MONTH_EN: string[] = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"];

    private static MONTH_DE: string[] = [
        "Januar",
        "Februar",
        "März",
        "April",
        "Mai",
        "Juni",
        "Juli",
        "August",
        "September",
        "Oktober",
        "November",
        "Dezember"];

    private static DAY_EN: string[] = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"];

    private static DAY_DE: string[] = [
        "Sonntag",
        "Montag",
        "Dienstag",
        "Mittwoch",
        "Donnerstag",
        "Freitag",
        "Samstag"];


    public static thousandSeparator(value: number): string {
        value = Math.round(value);
        var negative = value < 0;
        var str = Math.abs(value).toString();
        var res = "";
        var index = 0;
        for (var i = str.length - 1; i >= 0; --i) {
            res = str[i] + res;
            index++;
            if (index % 3 == 0 && i > 0) {
                res = "." + res;
            }
        }
        return negative ? "-" + res : res;
    }

    /**
     * 
     * @link https://gist.github.com/andrei-m/982927
     * 
     * @param a 
     * @param b 
     * @returns 
     */
    public static levenstein(a: string, b: string): number {
        if (!(a && b)) return (b || a).length;

        var m: any = [];
        var i: number;
        var j: number;

        for (i = 0; i <= b.length; m[i] = [i++]);
        for (j = 0; j <= a.length; m[0][j] = j++);

        for (i = 1; i <= b.length; i++) {
            for (j = 1; j <= a.length; j++) {
                m[i][j] = b.charAt(i - 1) == a.charAt(j - 1)
                    ? m[i - 1][j - 1]
                    : m[i][j] = Math.min(
                        m[i - 1][j - 1] + 1,
                        Math.min(m[i][j - 1] + 1, m[i - 1][j] + 1))
            }
        }

        return m[b.length][a.length];
    }

    public static trim(str: string, chars: string = "\\s") {
        return str.replace(new RegExp('^' + chars + '+|' + chars + '+$', 'g'), '');
    }

    public static rtrim(str: string, chars: string = "\\s") {
        return str.replace(new RegExp(chars + '+$'), '');
    }

    public static ltrim(str: string, chars: string = "\\s") {
        return str.replace(new RegExp('^' + chars + '+'), '');
    }

    public static stripTags(str: string): string {
        return (str || "").replace(/(<([^>]+)>)/gi, "");
    }

    public static fileSize(bytes: number, roundMB: Boolean = false): string {

        // const BYTE_TO_KILOBYTE: number = 0.000976563;
        const BYTE_TO_MB: number = 9.53674e-7;
        // const BYTE_TO_GIGABYTE: number = 9.31323e-10;

        var mb: number = Calc.precision(bytes * BYTE_TO_MB, 100);
        // if (mb > 1000)
        //     return Calc.precision(bytes * Calc.BYTE_TO_GIGABYTE, 100) + " GB";
        // else if (mb < 1)
        //     return Math.round(bytes * Calc.BYTE_TO_KILOBYTE) + " kB";
        // if (roundMB)
        // return Math.round(mb) + " MB";
        return mb.toFixed(2) + " MB";
    }

    public static ext(filename: string): string {
        var dotIndex: number = filename.lastIndexOf(".");
        return filename.substr(dotIndex + 1).toLowerCase();
    }

    public static month(month: number, lang: "de" | "en" = "de"): string {
        if (lang == "de")
            return Text.MONTH_DE[Calc.clamp(month, 0, 11)];
        return Text.MONTH_EN[Calc.clamp(month, 0, 11)];
    }

    public static day(day: number, lang: "de" | "en" = "de"): string {
        if (lang == "de")
            return Text.DAY_DE[Calc.clamp(day, 0, 6)];
        return Text.DAY_EN[Calc.clamp(day, 0, 6)];
    }

    public static escapeHtml(value: string): string {

        if (!value) {
            return "";
        }

        return (value + "").replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/\//g, '&#x2F;');
    }

    public static escapeAttr(value: string): string {
        if (!value) {
            return "";
        }

        return (value + "").replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    public static replaceNewline(str: string, replace: string = "<br>"): string {
        return str ? str.replace(/(?:\r\n|\r|\n)/g, replace) : "";
    }

    public static replaceAll(str: string, find: string, replace: string = ""): string {
        return str.replace(new RegExp(this.escapeRegExp(find), 'g'), replace);
    }

    public static escapeRegExp(str: string) {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    }

    public static toDate(str: string): Date {
        var year: number = parseInt(str.substr(0, 4));
        var month: number = parseInt(str.substr(5, 2)) - 1;
        var date: number = parseInt(str.substr(8, 2));
        if (str.length <= 10)
            return new Date(year, month, date);
        var hours: number = parseInt(str.substr(11, 2));
        var minutes: number = parseInt(str.substr(14, 2));
        var seconds: number = parseInt(str.substr(17, 2));
        return new Date(year, month, date, hours, minutes, seconds);
    }

    private static loremIpsumWords: string[] = "Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Maecenas porttitor congue massa. Fusce posuere, magna sed pulvinar ultricies, purus lectus malesuada libero, sit amet commodo magna eros quis urna. Nunc viverra imperdiet enim. Fusce est. Vivamus a tellus. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Proin pharetra nonummy pede. Mauris et orci. Aenean nec lorem. In porttitor. Donec laoreet nonummy augue. Suspendisse dui purus, scelerisque at, vulputate vitae, pretium mattis, nunc. Mauris eget neque at sem venenatis eleifend. Ut nonummy. Fusce aliquet pede non pede. Suspendisse dapibus lorem pellentesque magna. Integer nulla. Donec blandit feugiat ligula. Donec hendrerit, felis et imperdiet euismod, purus ipsum pretium metus, in lacinia nulla nisl eget sapien. Donec ut est in lectus consequat consequat. Etiam eget dui. Aliquam erat volutpat. Sed at lorem in nunc porta tristique. Proin nec augue. Quisque aliquam tempor magna. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Nunc ac magna. Maecenas odio dolor, vulputate vel, auctor ac, accumsan id, felis. Pellentesque cursus sagittis felis. Pellentesque porttitor, velit lacinia egestas auctor, diam eros tempus arcu, nec vulputate augue magna vel risus. Cras non magna vel ante adipiscing rhoncus. Vivamus a mi. Morbi neque. Aliquam erat volutpat. Integer ultrices lobortis eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Proin semper, ante vitae sollicitudin posuere, metus quam iaculis nibh, vitae scelerisque nunc massa eget pede. Sed velit urna, interdum vel, ultricies vel, faucibus at, quam. Donec elit est, consectetuer eget, consequat quis, tempus quis, wisi. In in nunc. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos hymenaeos. Donec ullamcorper fringilla eros. Fusce in sapien eu purus dapibus commodo. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Cras faucibus condimentum odio. Sed ac ligula. Aliquam at eros. Etiam at ligula et tellus ullamcorper ultrices. In fermentum, lorem non cursus porttitor, diam urna accumsan lacus, sed interdum wisi nibh nec nisl. Ut tincidunt volutpat urna. Mauris eleifend nulla eget mauris. Sed cursus quam id felis. Curabitur posuere quam vel nibh. Cras dapibus dapibus nisl. Vestibulum quis dolor a felis congue vehicula. Maecenas pede purus, tristique ac, tempus eget, egestas quis, mauris. Curabitur non eros. Nullam hendrerit bibendum justo. Fusce iaculis, est quis lacinia pretium, pede metus molestie lacus, at gravida wisi ante at libero. Quisque ornare placerat risus. Ut molestie magna at mi. Integer aliquet mauris et nibh. Ut mattis ligula posuere velit. Nunc sagittis. Curabitur varius fringilla nisl. Duis pretium mi euismod erat. Maecenas id augue. Nam vulputate. Duis a quam non neque lobortis malesuada. Praesent euismod. Donec nulla augue, venenatis scelerisque, dapibus a, consequat at, leo. Pellentesque libero lectus, tristique ac, consectetuer sit amet, imperdiet ut, justo. Sed aliquam odio vitae tortor. Proin hendrerit tempus arcu. In hac habitasse platea dictumst. Suspendisse potenti. Vivamus vitae massa adipiscing est lacinia sodales. Donec metus massa, mollis vel, tempus placerat, vestibulum condimentum, ligula. Nunc lacus metus, posuere eget, lacinia eu, varius quis, libero. Aliquam nonummy adipiscing augue".split(" ");

    public static loremIpsum(wordCount: number, random: boolean = false): string {
        var res = "";
        var offset = 0;
        if (random) {
            offset = Math.floor(Math.random() * 100);
        }
        for (var i = 0; i < wordCount; ++i) {
            res += this.loremIpsumWords[(i + offset) % (this.loremIpsumWords.length - 1)] + " ";
        }
        return res.trim();
    }

    public static randomString(length: number): string {
        var text = "";
        var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789 ";
        for (var i = 0; i < length; ++i)
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        return text;
    }

    public static fill(text: string, maxLength: number, fillChar: string): string {
        if (text.length >= maxLength)
            return text;

        for (var j: number = text.length; j < maxLength; ++j)
            text += fillChar.charAt(0);
        return text;
    }

    public static fillHead(text: string, maxLength: number, fillChar: string): string {
        if (text.length >= maxLength)
            return text;

        for (var i: number = text.length; i < maxLength; ++i)
            text = fillChar.charAt(0) + text;
        return text;
    }

    public static lines(text: string, maxCharsPerLine: number = 8, minHyphenLength: number = 3, separators: string = "\u00ad-\n "): string[] {
        text = text.trim();
        text = text.replace(/&shy;/gi, "\u00AD");
        text = text.replace(/\<br\s*\/?\>/gi, "\n");

        if (minHyphenLength <= 0)
            text = text.replace(/\u00AD/gi, "");

        var lines: string[] = [];
        var splitChars: string[] = [];
        var line = "";
        for (var i = 0; i < text.length; ++i) {
            var char = text.charAt(i);

            if (char === "\u00ad" && line.length < minHyphenLength) {

            }
            else if (separators.indexOf(char) !== -1) {
                if (line.length > 0) {
                    lines.push(line);
                    splitChars.push(char);
                    line = "";
                }
            }
            else {
                line += char;
            }
        }

        if (line) {
            lines.push(line);
            splitChars.push("");
        }

        // console.log(lines);
        // console.log(splitChars);

        var result: string[] = [];
        var resultLine: string = "";
        for (var i = 0; i < lines.length; ++i) {
            var line = lines[i];
            var char = splitChars[i];

            if (resultLine.length + line.length > maxCharsPerLine) {
                if (resultLine.trim().length > 0) {
                    if (splitChars[i - 1] === "\u00ad") {
                        resultLine += "-";
                    }
                    result.push(resultLine.trim());
                    resultLine = "";
                }
            }
            if (char != "\u00ad")
                line += char;
            resultLine += line;
        }
        if (resultLine) {
            result.push(resultLine.trim());
        }

        return result;
    }

    public static clamp(text: string, maxLength: number, ending: string = "...", minLength: number = 3, separators: string = " -+,._;/\\"): string {
        if (text.length < maxLength)
            return text;

        var t: string = text.substr(0, maxLength);
        var l: number = t.length;
        var m: number = separators.length;
        var breaked: boolean = false;
        for (var j: number = l - 3; j >= 0; j--) {
            var character: String = t.charAt(j);
            for (var i: number = 0; i < m; ++i) {
                if (separators.charAt(i) == character) {
                    breaked = true;
                    break;
                }
            }
            if (breaked)
                break;
        }
        if (!breaked)
            return t.substr(0, maxLength - 3) + ending;
        return text.substr(0, ++j) + ending;
    }
}